import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Loader2, AlertCircle, CheckCircle2, KeyRound, ArrowLeft, WifiOff } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Online/Offline Status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isOffline) {
      setError('Keine Internetverbindung. Bitte versuchen Sie es später erneut.');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError('');
    setResetSuccess(false);

    try {
      await authApi.requestPasswordReset(resetEmail);
      setResetSuccess(true);
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Fehler beim Senden der Anfrage');
    } finally {
      setResetLoading(false);
    }
  };

  // Logo mit Cache-Busting Version
  const logoVersion = "v2";
  const logoUrl = `/firecheck-logo.png?${logoVersion}`;

  // Passwort vergessen Ansicht
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom duration-500">
          {/* Logo */}
          <div className="flex flex-col items-center justify-center gap-3 mb-8">
            <img src={logoUrl} alt="FireCheck Logo" className="h-28 w-28 rounded-2xl object-contain" />
            <div className="text-center">
              <h1 className="font-heading font-black text-3xl">FireCheck</h1>
              <p className="text-sm text-muted-foreground">Brandschutz-Abnahme</p>
            </div>
          </div>

          <Card className="border-border/50">
            <CardHeader className="space-y-1">
              <div className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-primary" />
                <CardTitle className="font-heading text-2xl">Passwort vergessen</CardTitle>
              </div>
              <CardDescription>
                Geben Sie Ihre E-Mail-Adresse ein. Der Administrator wird Ihr Passwort zurücksetzen und Sie kontaktieren.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {resetSuccess ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 text-green-500">
                    <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Anfrage gesendet!</p>
                      <p className="text-sm opacity-90">Der Administrator wurde benachrichtigt und wird Sie kontaktieren.</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetSuccess(false);
                      setResetEmail('');
                    }}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Zurück zum Login
                  </Button>
                </div>
              ) : (
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  {resetError && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-red-500 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      {resetError}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="reset-email">E-Mail-Adresse</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="name@example.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                      className="h-12"
                      data-testid="reset-email-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 font-medium"
                    disabled={resetLoading}
                    data-testid="reset-submit-button"
                  >
                    {resetLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Senden...
                      </>
                    ) : (
                      'Anfrage senden'
                    )}
                  </Button>

                  <Button 
                    type="button"
                    variant="ghost" 
                    className="w-full"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetError('');
                    }}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Zurück zum Login
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom duration-500">
        {/* Logo */}
        <div className="flex flex-col items-center justify-center gap-3 mb-8">
          <img src={logoUrl} alt="FireCheck Logo" className="h-28 w-28 rounded-2xl object-contain" />
          <div className="text-center">
            <h1 className="font-heading font-black text-3xl">FireCheck</h1>
            <p className="text-sm text-muted-foreground">Brandschutz-Abnahme</p>
          </div>
        </div>

        <Card className="border-border/50">
          <CardHeader className="space-y-1">
            <CardTitle className="font-heading text-2xl">Anmelden</CardTitle>
            <CardDescription>
              Melden Sie sich mit Ihren Zugangsdaten an
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isOffline && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 text-amber-600 text-sm">
                  <WifiOff className="h-4 w-4" />
                  Keine Internetverbindung. Login nicht möglich.
                </div>
              )}
              
              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-red-500 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12"
                  data-testid="login-email-input"
                  disabled={isOffline}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12"
                  data-testid="login-password-input"
                  disabled={isOffline}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 font-medium"
                disabled={loading || isOffline}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Anmelden...
                  </>
                ) : isOffline ? (
                  <>
                    <WifiOff className="mr-2 h-4 w-4" />
                    Offline
                  </>
                ) : (
                  'Anmelden'
                )}
              </Button>
            </form>

            <div className="mt-6 space-y-3">
              <button 
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="w-full text-sm text-muted-foreground hover:text-primary transition-colors"
                data-testid="forgot-password-link"
              >
                Passwort vergessen?
              </button>
              
              <p className="text-sm text-muted-foreground text-center">
                Noch kein Konto?{' '}
                <Link 
                  to="/register" 
                  className="text-primary hover:underline font-medium"
                  data-testid="register-link"
                >
                  Registrieren
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Link 
            to="/super-admin" 
            className="text-xs text-muted-foreground hover:text-primary transition-colors"
            data-testid="super-admin-link"
          >
            Super-Admin Login →
          </Link>
        </div>

        <div className="mt-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20 space-y-3">
          <p className="text-xs text-amber-500">
            ⚠️ Diese Anwendung dient ausschliesslich zum Erstellen und direkten Versenden von Brandschutz-Abnahmeberichten. 
            Es handelt sich nicht um eine Datenbank zur Langzeitarchivierung.
          </p>
          <p className="text-xs text-amber-400/80">
            <strong>Hinweis:</strong> Die Checklisten sind nicht abschliessend und müssen eigenverantwortlich ergänzt werden. 
            Der Betreiber übernimmt keine Haftung für Fehler oder Unvollständigkeiten.
          </p>
        </div>

        <div className="text-center mt-4">
          <Link 
            to="/agb" 
            className="text-xs text-muted-foreground hover:text-primary transition-colors underline"
            data-testid="agb-link"
          >
            Allgemeine Geschäftsbedingungen (AGB)
          </Link>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-4">
          © 2026 FireCheck. Alle Rechte vorbehalten.
        </p>
      </div>
    </div>
  );
}
